/**
 * What the rundown tree's commands (`keyboard.ts`' `TreeCommand`) do, over
 * the editor session's row store and writer and the page's UI callbacks —
 * the view (`views/RundownEditorView.tsx`) binds `Ui` to its React state.
 *
 * Every write goes through `ui.commit(build, after)`: `build` makes the
 * patches from the tree as it is NOW, and may be called again later — when
 * the on-air protection asks first (`onAirGuard.ts`), the edit is rebuilt at
 * confirmation, so its HLC, ids and `ord` keys are minted against the tree
 * the operator confirmed on, not the one they started from. `after` gets the
 * patches that were applied.
 */
import type { RowPatch } from '@acp/domain/row.js';
import type { RowTree } from '@acp/domain/tree.js';
import type { NavState, TreeCommand } from './keyboard.js';
import { moveAnnouncement, planKeyboardMove } from './move.js';
import { createPatch, initialFields, movePatches, type NewPieceType, ordForInsert } from './patches.js';
import { displayName, type FlatRow } from './viewModel.js';
import type { Writer } from '@acp/api-client/writer.js';

export type Build = () => readonly RowPatch[];

/** Apply what `build` makes (asking first when it touches a protected part); `after` once applied. */
export type GuardedCommit = (build: Build, after: ((applied: readonly RowPatch[]) => void) | null) => void;

export type CommandSession = { readonly store: { readonly tree: RowTree }; readonly writer: Writer };

export type Ui = {
  readonly updateCollapsed: (update: (prev: ReadonlySet<string>) => ReadonlySet<string>) => void;
  readonly setNav: (nav: NavState) => void;
  readonly setConfirmDelete: (ids: readonly string[] | null) => void;
  readonly announce: (message: string) => void;
  readonly commit: GuardedCommit;
  /** Undo or redo, through the on-air protection. */
  readonly step: (direction: 'undo' | 'redo') => void;
  readonly displayOrder: readonly string[];
  /** The type a new piece or adlib starts as (the show style's first), or null when the editor knows none. */
  readonly newPieceType: NewPieceType | null;
};

/** `ids` and every ancestor of theirs, taken out of the collapsed set: what was just placed must be visible. */
export function revealed(tree: RowTree, collapsed: ReadonlySet<string>, ids: readonly string[]): ReadonlySet<string> {
  const next = new Set(collapsed);
  for (const id of ids) {
    const seen = new Set<string>();
    let current = tree.rows.get(id)?.parentId ?? null;
    while (current !== null && !seen.has(current)) {
      seen.add(current);
      next.delete(current);
      current = tree.rows.get(current)?.parentId ?? null;
    }
  }
  return next.size === collapsed.size ? collapsed : next;
}

function runCreate(session: CommandSession, rundownId: string, ui: Ui, command: Extract<TreeCommand, { type: 'create' }>): void {
  const parent = command.parentId ?? rundownId;
  ui.commit(
    () => [createPatch(session.writer, rundownId, command.kind, parent, ordForInsert(session.store.tree, parent, command.afterId, session.writer.ordTag), initialFields(command.kind, ui.newPieceType))],
    (applied) => {
      const rowId = applied[0]?.rowId;
      if (rowId === undefined) return;
      ui.updateCollapsed((prev) => revealed(session.store.tree, prev, [rowId]));
      ui.setNav({ focusedId: rowId, selected: [rowId], anchorId: rowId, editingId: rowId });
    },
  );
}

function runMove(session: CommandSession, rundownId: string, ui: Ui, command: Extract<TreeCommand, { type: 'move' }>): void {
  const plan = () => planKeyboardMove(session.store.tree, rundownId, ui.displayOrder, command.ids, command.direction, session.writer.ordTag);
  if (plan() === null) {
    ui.announce('Cannot move further.');
    return;
  }
  ui.commit(
    () => {
      const moves = plan();
      return moves === null ? [] : movePatches(session.writer, moves);
    },
    () => {
      // Moved into a collapsed parent, the rows would vanish and take the focus with them.
      ui.updateCollapsed((prev) => revealed(session.store.tree, prev, command.ids));
      ui.announce(moveAnnouncement(session.store.tree, command.ids));
    },
  );
}

function toggled(collapsed: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(collapsed);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** What each tree command does to the session and the page's state. */
export function runCommand(session: CommandSession, rundownId: string, ui: Ui, command: TreeCommand): void {
  switch (command.type) {
    case 'create':
      runCreate(session, rundownId, ui, command);
      return;
    case 'move':
      runMove(session, rundownId, ui, command);
      return;
    case 'delete':
      ui.setConfirmDelete(command.ids);
      return;
    case 'undo':
    case 'redo':
      ui.step(command.type);
      return;
    case 'toggle-collapse':
      ui.updateCollapsed((prev) => toggled(prev, command.id));
      return;
  }
}

/** The names a confirmation shows for `ids`, as the tree names them (the id when a row is not shown). */
export function namesOf(rows: readonly FlatRow[], ids: readonly string[]): string[] {
  return ids.map((id) => {
    const row = rows.find((r) => r.id === id);
    return row === undefined ? id : displayName(row.view);
  });
}
